import { BasicInput } from "@/view/components/BasicInput";
import { Commission } from "@/app/entities/Team";
import { useState, useMemo } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/view/components/TabsTable";
import { moneyMask, formatMoneyValue } from "@/app/utils/formatMoneyValue";
import { ProductsConfigResponseNew } from "@/app/services/configService/getProductsConfig";
import { UserRoleEnum } from "@/app/services/usersService/user";
import { PartnerType } from "@/app/utils/partners.enum";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import { useProductVisibility } from "@/app/hooks/useProductVisibility";

export type CommissionType = "seller" | "leader" | "supervisor";

interface CommissionFormProps {
  commission: Commission[];
  onChange?: (
    index: number,
    type: CommissionType,
    value: number | undefined,
    valueIndex?: number,
  ) => void;
  readOnly?: boolean;
  visibleCommissions?: CommissionType[];
  productsConfig?: ProductsConfigResponseNew | null;
  userRole?: UserRoleEnum;
}

export const CommissionForm = ({
  commission,
  onChange,
  readOnly = false,
  visibleCommissions = ["supervisor", "leader", "seller"],
}: CommissionFormProps) => {
  const { getProductLabel } = useProductsCatalog();
  const { isProductVisible } = useProductVisibility();

  const visibleTabs = useMemo(() => {
    const tabs: string[] = [];
    const checkTab = (partner: PartnerType, tabName: string) => {
      if (isProductVisible(partner)) {
        tabs.push(tabName);
      }
    };
    checkTab(PartnerType.FGTS, "fgts");
    checkTab(PartnerType.CREFAZ, "crefaz");
    checkTab(PartnerType.SALVCAR, "salvcar");
    checkTab(PartnerType.CREFISA, "crefisa");
    checkTab(PartnerType.CREFISA_INSS, "crefisa_inss");
    checkTab(PartnerType.PAN, "pan");
    checkTab(PartnerType.C6, "c6");
    checkTab(PartnerType.CLT, "clt");
    return tabs;
  }, [isProductVisible]);

  const [activeTab, setActiveTab] = useState(visibleTabs[0] || "fgts");

  const isVisible = (type: CommissionType) => visibleCommissions.includes(type);

  const configuredTables = (commission || []).map((table, index) => ({
    ...table,
    index,
  }));

  const isCltTable = (t: { type?: string; name?: string }) =>
    t.type?.toUpperCase() === "CLT";

  const isCrefisaInssTable = (t: { type?: string; name?: string }) => {
    if (t.type?.toUpperCase() === PartnerType.CREFISA_INSS) return true;
    const name = t.name?.toLowerCase() || "";
    return name.includes("crefisa") && name.includes("inss");
  };

  const fgtsTables = configuredTables.filter(
    (t) =>
      !isCltTable(t) &&
      !t.name?.toLowerCase().includes("crefaz") &&
      !t.name?.toLowerCase().includes("salvcar") &&
      !t.name?.toLowerCase().includes("crefisa") &&
      !t.name?.toLowerCase().includes("pan") &&
      !t.name?.toLowerCase().includes("c6"),
  );
  const crefazTables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes("crefaz"),
  );
  const salvcarTables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes("salvcar"),
  );
  const crefisaTables = configuredTables.filter(
    (t) => t.name?.toLowerCase().includes("crefisa") && !isCrefisaInssTable(t),
  );
  const crefisaInssTables = configuredTables.filter(isCrefisaInssTable);
  const panTables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes("pan"),
  );
  const c6Tables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes("c6"),
  );
  const cltTables = configuredTables.filter(isCltTable);

  const tabsConfig = [
    {
      id: "fgts",
      label: "FGTS",
      partner: PartnerType.FGTS,
      tables: fgtsTables,
      isSalvcar: false,
      isCrefaz: false,
      isCrefisa: false,
      isPan: false,
      isC6: false,
      isClt: false,
    },
    {
      id: "crefaz",
      label: "Crefaz",
      partner: PartnerType.CREFAZ,
      tables: crefazTables,
      isSalvcar: false,
      isCrefaz: true,
      isCrefisa: false,
      isPan: false,
      isC6: false,
      isClt: false,
    },
    {
      id: "salvcar",
      label: "Salvcar",
      partner: PartnerType.SALVCAR,
      tables: salvcarTables,
      isSalvcar: true,
      isCrefaz: false,
      isCrefisa: false,
      isPan: false,
      isC6: false,
      isClt: false,
    },
    {
      id: "crefisa",
      label: "Crefisa",
      partner: PartnerType.CREFISA,
      tables: crefisaTables,
      isSalvcar: false,
      isCrefaz: false,
      isCrefisa: true,
      isPan: false,
      isC6: false,
      isClt: false,
    },
    {
      id: "crefisa_inss",
      label: "Crefisa INSS",
      partner: PartnerType.CREFISA_INSS,
      tables: crefisaInssTables,
      isSalvcar: false,
      isCrefaz: false,
      isCrefisa: true,
      isPan: false,
      isC6: false,
      isClt: false,
    },
    {
      id: "pan",
      label: "PAN CLT",
      partner: PartnerType.PAN,
      tables: panTables,
      isSalvcar: false,
      isCrefaz: false,
      isCrefisa: false,
      isPan: true,
      isC6: false,
      isClt: false,
    },
    {
      id: "c6",
      label: "C6 Bank",
      partner: PartnerType.C6,
      tables: c6Tables,
      isSalvcar: false,
      isCrefaz: false,
      isCrefisa: false,
      isPan: false,
      isC6: true,
      isClt: false,
    },
    {
      id: "clt",
      label: "Grandino CLT",
      partner: PartnerType.CLT,
      tables: cltTables,
      isSalvcar: false,
      isCrefaz: false,
      isCrefisa: false,
      isPan: false,
      isC6: false,
      isClt: true,
    },
  ];

  const renderCards = (
    tables: typeof configuredTables,
    isSalvcar = false,
    isCrefaz = false,
    isCrefisa = false,
    isPan = false,
    showTitle = true,
    isClt = false,
    isC6 = false,
  ) => {
    const isCurrency = isSalvcar;
    const isSingleCommission = isPan || isCrefisa || isC6;

    const validateAndChange = (
      index: number,
      type: "seller" | "leader" | "supervisor",
      newValue: number | undefined,
      _context: any,
      valueIndex?: number,
    ) => {
      onChange?.(index, type, newValue, valueIndex);
    };

    const parseInputValue = (inputValue: string): number | undefined => {
      const cleaned = isCurrency
        ? inputValue.replace(/\D/g, "")
        : inputValue.replace(",", ".");
      if (cleaned === "") return undefined;
      const num = Number(cleaned);
      return isNaN(num) ? undefined : num;
    };

    return (
      <div className="flex flex-col gap-5 py-5">
        {tables.map(
          ({
            index,
            name,
            seller,
            leader,
            supervisor,
            table,
            max,
            values,
            ...rest
          }) => {
            const placeholderSeller = (rest as any).placeholderSeller;
            const placeholderLeader = (rest as any).placeholderLeader;
            const placeholderSupervisor = (rest as any).placeholderSupervisor;
            if (
              values &&
              values.length > 0 &&
              (isCrefaz || isSalvcar || isCrefisa || isPan || isC6 || isClt)
            ) {
              return values.map((value, valueIndex) => {
                const valuePlaceholderSeller =
                  (value as any).placeholderSeller ?? placeholderSeller;
                const valuePlaceholderLeader =
                  (value as any).placeholderLeader ?? placeholderLeader;
                const valuePlaceholderSupervisor =
                  (value as any).placeholderSupervisor ?? placeholderSupervisor;
                return (
                  <div
                    key={`${table}-${valueIndex}`}
                    className="flex w-full flex-col rounded-xl border bg-card p-4 text-card-foreground"
                  >
                    <h3 className="text-lg font-semibold">
                      {isSingleCommission
                        ? name
                        : isCurrency
                          ? `${isSalvcar ? "Veículos a" : "A"} partir de ${formatMoneyValue(value.value)}`
                          : `A partir de ${value.value} parcelas`}
                    </h3>
                    {isClt && (
                      <p className="text-xs text-black04">Tabela {name}</p>
                    )}
                    {!!value?.max && (
                      <p className="text-xs text-black04">
                        Máximo{" "}
                        {isCurrency
                          ? formatMoneyValue(value.max || 0)
                          : `${value.max}%`}
                      </p>
                    )}
                    <div className="mt-3 flex gap-4">
                      {isVisible("seller") && (
                        <div className="min-w-0 flex-1">
                          <div className="flex gap-2">
                            <p className="text-xs font-bold">Comissão</p>
                            <div className="flex items-center gap-1">
                              <div className="h-[5px] w-[5px] rounded-full bg-blue-600" />
                              <p className="text-xs font-bold text-info">
                                Vendedor
                              </p>
                            </div>
                          </div>
                          <BasicInput
                            name={`commission_${index}_values_${valueIndex}_seller`}
                            {...(isCurrency
                              ? {
                                  value:
                                    value.seller !== undefined &&
                                    value.seller !== null
                                      ? formatMoneyValue(value.seller)
                                      : "",
                                }
                              : {
                                  defaultValue:
                                    value.seller !== undefined &&
                                    value.seller !== null
                                      ? value.seller
                                      : "",
                                })}
                            placeholder={
                              valuePlaceholderSeller
                                ? isCurrency
                                  ? formatMoneyValue(valuePlaceholderSeller)
                                  : String(valuePlaceholderSeller)
                                : undefined
                            }
                            onChange={(e) => {
                              const val = parseInputValue(e.target.value);
                              validateAndChange(
                                index,
                                "seller",
                                val,
                                value,
                                valueIndex,
                              );
                            }}
                            style={{ backgroundColor: "#F7F7F7" }}
                            variant={isCurrency ? "default" : "percentage"}
                            onInput={isCurrency ? moneyMask : undefined}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                      {isVisible("leader") && (
                        <div className="min-w-0 flex-1">
                          <div className="flex gap-2">
                            <p className="text-xs font-bold">Comissão</p>
                            <div className="flex items-center gap-1">
                              <div className="h-[5px] w-[5px] rounded-full bg-red-500" />
                              <p className="text-xs font-bold text-red-500">
                                Líder
                              </p>
                            </div>
                          </div>
                          <BasicInput
                            name={`commission_${index}_values_${valueIndex}_leader`}
                            {...(isCurrency
                              ? {
                                  value:
                                    value.leader !== undefined &&
                                    value.leader !== null
                                      ? formatMoneyValue(value.leader)
                                      : "",
                                }
                              : {
                                  defaultValue:
                                    value.leader !== undefined &&
                                    value.leader !== null
                                      ? value.leader
                                      : "",
                                })}
                            placeholder={
                              valuePlaceholderLeader
                                ? isCurrency
                                  ? formatMoneyValue(valuePlaceholderLeader)
                                  : String(valuePlaceholderLeader)
                                : undefined
                            }
                            onChange={(e) => {
                              const val = parseInputValue(e.target.value);
                              validateAndChange(
                                index,
                                "leader",
                                val,
                                value,
                                valueIndex,
                              );
                            }}
                            style={{ backgroundColor: "#F7F7F7" }}
                            variant={isCurrency ? "default" : "percentage"}
                            onInput={isCurrency ? moneyMask : undefined}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                      {isVisible("supervisor") && (
                        <div className="min-w-0 flex-1">
                          <div className="flex gap-2">
                            <p className="text-xs font-bold">Comissão</p>
                            <div className="flex items-center gap-1">
                              <div className="h-[5px] w-[5px] rounded-full bg-yellow-500" />
                              <p className="text-xs font-bold text-yellow-500">
                                Supervisor
                              </p>
                            </div>
                          </div>
                          <BasicInput
                            name={`commission_${index}_values_${valueIndex}_supervisor`}
                            {...(isCurrency
                              ? {
                                  value:
                                    value.supervisor !== undefined &&
                                    value.supervisor !== null
                                      ? formatMoneyValue(value.supervisor)
                                      : "",
                                }
                              : {
                                  defaultValue:
                                    value.supervisor !== undefined &&
                                    value.supervisor !== null
                                      ? value.supervisor
                                      : "",
                                })}
                            placeholder={
                              valuePlaceholderSupervisor
                                ? isCurrency
                                  ? formatMoneyValue(valuePlaceholderSupervisor)
                                  : String(valuePlaceholderSupervisor)
                                : undefined
                            }
                            onChange={(e) => {
                              const val = parseInputValue(e.target.value);
                              validateAndChange(
                                index,
                                "supervisor",
                                val,
                                value,
                                valueIndex,
                              );
                            }}
                            style={{ backgroundColor: "#F7F7F7" }}
                            variant={isCurrency ? "default" : "percentage"}
                            onInput={isCurrency ? moneyMask : undefined}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            }

            return (
              <div
                key={table}
                className="flex w-full flex-col rounded-xl border bg-card p-4 text-card-foreground"
              >
                {showTitle && (
                  <h3 className="text-lg font-semibold">
                    {isClt ? `${name} parcelas` : name}
                  </h3>
                )}
                {showTitle && !!max && (
                  <p className="text-xs text-black04">
                    Máximo {isCurrency ? formatMoneyValue(max || 0) : `${max}%`}
                  </p>
                )}
                <div className="mt-3 flex gap-4">
                  {isVisible("seller") && (
                    <div className="min-w-0 flex-1">
                      <div className="flex gap-2">
                        <p className="text-xs font-bold">Comissão</p>
                        <div className="flex items-center gap-1">
                          <div className="h-[5px] w-[5px] rounded-full bg-blue-600" />
                          <p className="text-xs font-bold text-info">
                            Vendedor
                          </p>
                        </div>
                      </div>
                      <BasicInput
                        name={`commission_${index}_seller`}
                        {...(isCurrency
                          ? {
                              value:
                                seller !== undefined && seller !== null
                                  ? formatMoneyValue(seller)
                                  : "",
                            }
                          : {
                              defaultValue:
                                seller !== undefined && seller !== null
                                  ? seller
                                  : "",
                            })}
                        placeholder={
                          placeholderSeller
                            ? isCurrency
                              ? formatMoneyValue(placeholderSeller)
                              : String(placeholderSeller)
                            : undefined
                        }
                        onChange={(e) => {
                          const val = parseInputValue(e.target.value);
                          validateAndChange(index, "seller", val, {
                            supervisor,
                            leader,
                            seller,
                            max,
                          });
                        }}
                        style={{ backgroundColor: "#F7F7F7" }}
                        variant={isCurrency ? "default" : "percentage"}
                        onInput={isCurrency ? moneyMask : undefined}
                        disabled={readOnly}
                      />
                    </div>
                  )}
                  {isVisible("leader") && (
                    <div className="min-w-0 flex-1">
                      <div className="flex gap-2">
                        <p className="text-xs font-bold">Comissão</p>
                        <div className="flex items-center gap-1">
                          <div className="h-[5px] w-[5px] rounded-full bg-red-500" />
                          <p className="text-xs font-bold text-red-500">
                            Líder
                          </p>
                        </div>
                      </div>
                      <BasicInput
                        name={`commission_${index}_leader`}
                        {...(isCurrency
                          ? {
                              value:
                                leader !== undefined && leader !== null
                                  ? formatMoneyValue(leader)
                                  : "",
                            }
                          : {
                              defaultValue:
                                leader !== undefined && leader !== null
                                  ? leader
                                  : "",
                            })}
                        placeholder={
                          placeholderLeader
                            ? isCurrency
                              ? formatMoneyValue(placeholderLeader)
                              : String(placeholderLeader)
                            : undefined
                        }
                        onChange={(e) => {
                          const val = parseInputValue(e.target.value);
                          validateAndChange(index, "leader", val, {
                            supervisor,
                            leader,
                            seller,
                            max,
                          });
                        }}
                        style={{ backgroundColor: "#F7F7F7" }}
                        variant={isCurrency ? "default" : "percentage"}
                        onInput={isCurrency ? moneyMask : undefined}
                        disabled={readOnly}
                      />
                    </div>
                  )}
                  {isVisible("supervisor") && (
                    <div className="min-w-0 flex-1">
                      <div className="flex gap-2">
                        <p className="text-xs font-bold">Comissão</p>
                        <div className="flex items-center gap-1">
                          <div className="h-[5px] w-[5px] rounded-full bg-yellow-500" />
                          <p className="text-xs font-bold text-yellow-500">
                            Supervisor
                          </p>
                        </div>
                      </div>
                      <BasicInput
                        name={`commission_${index}_supervisor`}
                        {...(isCurrency
                          ? {
                              value:
                                supervisor !== undefined && supervisor !== null
                                  ? formatMoneyValue(supervisor)
                                  : "",
                            }
                          : {
                              defaultValue:
                                supervisor !== undefined && supervisor !== null
                                  ? supervisor
                                  : "",
                            })}
                        placeholder={
                          placeholderSupervisor
                            ? isCurrency
                              ? formatMoneyValue(placeholderSupervisor)
                              : String(placeholderSupervisor)
                            : undefined
                        }
                        onChange={(e) => {
                          const val = parseInputValue(e.target.value);
                          validateAndChange(index, "supervisor", val, {
                            supervisor,
                            leader,
                            seller,
                            max,
                          });
                        }}
                        style={{ backgroundColor: "#F7F7F7" }}
                        variant={isCurrency ? "default" : "percentage"}
                        onInput={isCurrency ? moneyMask : undefined}
                        disabled={readOnly}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          },
        )}
      </div>
    );
  };

  return (
    <Tabs
      defaultValue={activeTab}
      value={activeTab}
      onValueChange={setActiveTab}
    >
      <div className="pt-6">
        <TabsList className="flex w-full items-center gap-8 overflow-x-auto overflow-y-hidden">
          {tabsConfig
            .filter((tab) => visibleTabs.includes(tab.id))
            .map((tab) => (
              <TabsTrigger
                key={tab.id}
                className="shrink-0 whitespace-nowrap pb-2 text-xl data-[state=active]:border-b-2"
                value={tab.id}
              >
                {getProductLabel(tab.partner)}
              </TabsTrigger>
            ))}
        </TabsList>
      </div>

      {tabsConfig
        .filter((tab) => visibleTabs.includes(tab.id))
        .map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {renderCards(
              tab.tables,
              tab.isSalvcar,
              tab.isCrefaz,
              tab.isCrefisa,
              tab.isPan,
              true,
              tab.isClt,
              tab.isC6,
            )}
          </TabsContent>
        ))}
    </Tabs>
  );
};
